import { Icon } from "@iconify/react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Permissions } from "@/auth/permissions";
import { usePermission } from "@/auth/session-provider";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useI18n } from "@/locales/i18n-provider";
import { navigationGroups, systemConfigurationNavigation } from "@/surfaces/standalone/navigation/navigation-model";
import { useVisibleNavigationGroups, useVisibleNavigationItems } from "@/surfaces/standalone/navigation/use-navigation";

type WorkbenchCommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type WorkbenchCommand = {
  current: boolean;
  href: string;
  icon: string;
  id: string;
  keywords: string[];
  label: string;
};

type WorkbenchCommandGroup = {
  heading: string;
  id: string;
  items: WorkbenchCommand[];
};

type SearchScope = "all" | "tickets" | "customers" | "agents";

type SearchScopeOption = {
  icon: string;
  label: string;
  scope: SearchScope;
};

/** 零输入时提供快捷导航；输入后只选择搜索范围，实际搜索交给独立搜索页。 */
export function WorkbenchCommandPalette({ open, onOpenChange }: WorkbenchCommandPaletteProps) {
  const isLargeScreen = useMediaQuery("(min-width: 1024px)");
  const { can } = usePermission();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const visibleGroups = useVisibleNavigationGroups(navigationGroups);
  const visibleSettings = useVisibleNavigationItems(systemConfigurationNavigation);
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onOpenChange, open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const routeGroups = useMemo<WorkbenchCommandGroup[]>(() => {
    const workspaceGroups = visibleGroups
      .filter((group) => group.labelKey !== "navigation.group.system")
      .map((group) => ({
        heading: t(group.labelKey),
        id: group.labelKey,
        items: group.items.flatMap((item) => {
          if (!item.href) return [];

          const href = item.href;
          const label = t(item.labelKey);
          return [
            {
              current: location.pathname === href,
              href,
              icon: item.icon,
              id: `route:${href}`,
              keywords: [label, href, t(group.labelKey)],
              label,
            },
          ];
        }),
      }))
      .filter((group) => group.items.length > 0);

    const settingsItems = visibleSettings.flatMap((item) => {
      if (!item.href) return [];

      const href = item.href;
      const label = t(item.labelKey);
      return [
        {
          current: location.pathname === href,
          href,
          icon: item.icon,
          id: `route:${href}`,
          keywords: [label, href, t("shell.systemConfiguration")],
          label,
        },
      ];
    });

    return [
      ...workspaceGroups,
      ...(settingsItems.length
        ? [
            {
              heading: t("shell.systemConfiguration"),
              id: "system-configuration",
              items: settingsItems,
            },
          ]
        : []),
    ];
  }, [location.pathname, t, visibleGroups, visibleSettings]);

  const searchScopes = useMemo<SearchScopeOption[]>(
    () => [
      { icon: "tabler:world-search", label: t("shell.commandPalette.scope.all"), scope: "all" },
      ...(can(Permissions.tickets.view) ? [{ icon: "tabler:ticket", label: t("shell.commandPalette.scope.tickets"), scope: "tickets" as const }] : []),
      ...(can(Permissions.customers.view) ? [{ icon: "tabler:user", label: t("shell.commandPalette.scope.customers"), scope: "customers" as const }] : []),
      ...(can(Permissions.agents.view) ? [{ icon: "tabler:user-cog", label: t("shell.commandPalette.scope.agents"), scope: "agents" as const }] : []),
    ],
    [can, t],
  );

  const selectCommand = (href: string) => {
    navigate(href);
    onOpenChange(false);
  };

  const startSearch = (scope: SearchScope) => {
    const searchParams = new URLSearchParams({ q: normalizedQuery, scope });
    selectCommand(`/search?${searchParams.toString()}`);
  };

  if (!isLargeScreen) return null;

  return (
    <CommandDialog
      className="top-3.5 origin-top max-w-160!"
      description={t("shell.commandPalette.description")}
      open={open}
      title={t("shell.commandPalette.title")}
      onOpenChange={onOpenChange}
    >
      <Command shouldFilter={false}>
        <CommandInput
          aria-label={t("shell.commandPalette.placeholder")}
          placeholder={t("shell.commandPalette.placeholder")}
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>{t("shell.commandPalette.empty")}</CommandEmpty>

          {normalizedQuery ? (
            <CommandGroup heading={t("shell.commandPalette.searchFor", { query: normalizedQuery })}>
              {searchScopes.map((option, index) => (
                <CommandItem key={option.scope} value={`search:${option.scope}`} onSelect={() => startSearch(option.scope)}>
                  <Icon aria-hidden icon={option.icon} />
                  <span>{option.label}</span>
                  {index === 0 ? <CommandShortcut>↵</CommandShortcut> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          ) : (
            routeGroups.map((group, index) => (
              <div key={group.id}>
                {index > 0 ? <CommandSeparator /> : null}
                <CommandGroup heading={group.heading}>
                  {group.items.map((item) => (
                    <CommandItem key={item.id} keywords={item.keywords} value={item.id} onSelect={() => selectCommand(item.href)}>
                      <Icon aria-hidden icon={item.icon} />
                      <span className="truncate">{item.label}</span>
                      {item.current ? <CommandShortcut>{t("shell.commandPalette.current")}</CommandShortcut> : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            ))
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
